import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  tokens,
  makeStyles,
  Button,
  Text,
  Spinner,
  Caption1,
  Tooltip,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Avatar,
  Popover,
  PopoverTrigger,
  PopoverSurface,
} from '@fluentui/react-components';
import {
  SendRegular,
  ChatRegular,
  CopyRegular,
  CheckmarkRegular,
  ChatSparkleRegular,
  CodeRegular,
  PeopleRegular,
  ArrowUpRegular,
  ArrowDownRegular,
  StopRegular,
  ArrowSyncRegular,
  EditRegular,
  ArrowDownloadRegular,
  AddRegular,
  DismissRegular,
  InfoRegular,
} from '@fluentui/react-icons';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Message, ModelConfig, TokenUsage, Agent } from '../types';

// --- Styles ---

const useStyles = makeStyles({
  chatWindow: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    background: tokens.colorNeutralBackground1,
    overflow: 'hidden',
    borderRadius: tokens.borderRadiusXLarge,
    margin: tokens.spacingHorizontalXXL,
    boxShadow: tokens.shadow8,
    '@media (max-width: 768px)': {
      margin: 0,
      borderRadius: 0,
      minHeight: '100vh',
      height: '100vh',
      maxHeight: '100vh',
    },
  },
  chatMessages: {
    flexGrow: 1,
    overflowY: 'auto',
    padding: tokens.spacingHorizontalXXL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    background: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusXLarge,
    '@media (max-width: 768px)': {
      padding: tokens.spacingHorizontalM,
      borderRadius: 0,
      minHeight: 0,
      maxHeight: '100vh',
    },
  },
  messageRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    alignItems: 'flex-start',
    maxWidth: '100%',
  },
  messageRowUser: {
    flexDirection: 'row-reverse',
  },
  messageAvatar: {
    flexShrink: 0,
    marginTop: '4px',
  },
  messageColumn: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: '75%',
    minWidth: 0,
    '@media (max-width: 768px)': {
      maxWidth: '85%',
    },
  },
  messageColumnUser: {
    alignItems: 'flex-end',
  },
  messageBubble: {
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderRadius: '20px',
    boxShadow: tokens.shadow2,
    wordWrap: 'break-word',
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase400,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
  },
  userMessage: {
    background: tokens.colorBrandBackground2,
    color: tokens.colorNeutralForeground1,
    borderBottomRightRadius: '6px',
  },
  assistantMessage: {
    background: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground1,
    borderBottomLeftRadius: '6px',
  },
  agentNameLabel: {
    marginBottom: '4px',
    color: tokens.colorBrandForeground1,
  },
  messageContent: {
    '& p': { marginTop: 0, marginBottom: '8px' },
    '& p:last-child': { marginBottom: 0 },
    '& ul, & ol': { paddingLeft: '20px', marginTop: '4px', marginBottom: '8px' },
    '& li': { marginBottom: '2px' },
    '& a': { color: tokens.colorBrandForeground1 },
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      marginTop: '8px',
      marginBottom: '8px',
      fontSize: tokens.fontSizeBase200,
    },
    '& th, & td': {
      border: `1px solid ${tokens.colorNeutralStroke2}`,
      padding: '6px 10px',
      textAlign: 'left',
    },
    '& th': {
      background: tokens.colorNeutralBackground4,
      fontWeight: tokens.fontWeightSemibold,
    },
  },
  codeBlockContainer: {
    position: 'relative',
    borderRadius: '8px',
    overflow: 'hidden',
    marginTop: '8px',
    marginBottom: '8px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  codeBlockHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 12px',
    background: '#1e1e2e',
    color: '#a0a0b0',
    fontSize: '12px',
    fontFamily: 'monospace',
  },
  copyButton: {
    minWidth: 'auto',
    padding: '2px 8px',
    height: '24px',
    color: '#a0a0b0',
    ':hover': { color: '#ffffff', background: 'rgba(255,255,255,0.1)' } as Record<string, string>,
  },
  inlineCode: {
    fontFamily: '"Cascadia Code", "Fira Code", "Consolas", monospace',
    fontSize: '0.9em',
    padding: '2px 6px',
    borderRadius: '4px',
    background: tokens.colorNeutralBackground4,
    color: tokens.colorBrandForeground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  messageFooter: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginTop: '4px',
    minHeight: '24px',
  },
  messageActions: {
    display: 'flex',
    gap: '2px',
    opacity: 0,
    transition: 'opacity 0.15s',
  },
  messageRowHover: {
    ':hover .msgActions': { opacity: '1' } as Record<string, string>,
  },
  actionButton: {
    minWidth: 'auto',
    minHeight: 'auto',
    padding: '2px 6px',
    height: '24px',
  },
  editArea: {
    width: '100%',
    minHeight: '60px',
    padding: '8px 12px',
    fontSize: tokens.fontSizeBase300,
    fontFamily: 'inherit',
    borderRadius: '12px',
    border: `1px solid ${tokens.colorBrandStroke1}`,
    background: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    resize: 'vertical',
    outline: 'none',
    boxSizing: 'border-box',
  },
  editActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalS,
  },
  chatInputArea: {
    display: 'flex',
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalXXL}`,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    background: tokens.colorNeutralBackground2,
    borderBottomLeftRadius: tokens.borderRadiusXLarge,
    borderBottomRightRadius: tokens.borderRadiusXLarge,
    gap: tokens.spacingHorizontalM,
    flexDirection: 'column',
    flexShrink: 0,
    '@media (max-width: 768px)': {
      paddingLeft: tokens.spacingHorizontalL,
      paddingRight: tokens.spacingHorizontalL,
    },
  },
  inputRow: {
    display: 'flex',
    alignItems: 'flex-end',
    width: '100%',
    gap: tokens.spacingHorizontalM,
  },
  textareaWrapper: {
    flexGrow: 1,
    position: 'relative',
  },
  textarea: {
    width: '100%',
    minHeight: '44px',
    maxHeight: '192px',
    padding: '10px 16px',
    fontSize: tokens.fontSizeBase400,
    fontFamily: 'inherit',
    borderRadius: '22px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    background: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    resize: 'none',
    overflowY: 'hidden',
    lineHeight: '24px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s, height 0.1s ease',
    ':focus': {
      borderColor: tokens.colorBrandStroke1,
    } as Record<string, string>,
  },
  sendButton: {
    background: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    borderRadius: '24px',
    minWidth: '44px',
    minHeight: '44px',
    flexShrink: 0,
    ':hover': {
      background: tokens.colorBrandBackgroundHover,
    } as Record<string, string>,
  },
  stopButton: {
    borderRadius: '24px',
    minWidth: '44px',
    minHeight: '44px',
    flexShrink: 0,
  },
  sendingIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    alignSelf: 'flex-start',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  typingDots: {
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
  },
  landingPage: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: tokens.spacingHorizontalXXL,
    background: tokens.colorNeutralBackground2,
  },
  landingIcon: {
    fontSize: '56px',
    color: tokens.colorBrandForeground1,
    marginBottom: tokens.spacingVerticalL,
  },
  landingTitle: {
    marginBottom: tokens.spacingVerticalM,
  },
  landingSubtitle: {
    color: tokens.colorNeutralForeground2,
    marginBottom: tokens.spacingVerticalXXL,
    maxWidth: '480px',
  },
  starterGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
    maxWidth: '520px',
    width: '100%',
    '@media (max-width: 600px)': {
      gridTemplateColumns: '1fr',
    },
  },
  starterCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '14px 16px',
    borderRadius: '12px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    background: tokens.colorNeutralBackground1,
    cursor: 'default',
    textAlign: 'left',
    transition: 'border-color 0.15s',
    ':hover': {
      borderColor: tokens.colorBrandStroke1,
    } as Record<string, string>,
  },
  starterIcon: {
    color: tokens.colorBrandForeground1,
    fontSize: '20px',
    flexShrink: 0,
    marginTop: '2px',
  },
  starterText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    lineHeight: tokens.lineHeightBase200,
  },
  emptyChatContainer: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: tokens.spacingHorizontalXXL,
    color: tokens.colorNeutralForeground3,
    gap: tokens.spacingVerticalM,
  },
  emptyChatIcon: {
    fontSize: '48px',
    color: tokens.colorNeutralForeground4,
  },
  chatToolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalXXL}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    background: tokens.colorNeutralBackground2,
    flexShrink: 0,
    gap: tokens.spacingHorizontalM,
    '@media (max-width: 768px)': {
      paddingLeft: tokens.spacingHorizontalM,
      paddingRight: tokens.spacingHorizontalM,
      flexWrap: 'wrap',
    },
  },
  toolbarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flexShrink: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
  toolbarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexShrink: 0,
  },
  modelPillContainer: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    overflowX: 'auto',
    flexShrink: 1,
    padding: '2px 0',
    scrollbarWidth: 'none',
    minWidth: 0,
  },
  modelPill: {
    whiteSpace: 'nowrap',
    flexShrink: 0,
    minWidth: 'auto',
    fontSize: tokens.fontSizeBase200,
  },
  tokenDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  tokenBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: `2px ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    background: tokens.colorNeutralBackground4,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  costBadge: {
    display: 'flex',
    alignItems: 'center',
    padding: `2px ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    background: tokens.colorNeutralBackground4,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorBrandForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  loadingSpinner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: tokens.spacingVerticalXXL,
  },
  errorMessage: {
    margin: tokens.spacingHorizontalL,
  },
  agentInfoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexShrink: 0,
  },
  speedIndicator: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground4,
  },
});

// --- CodeBlock ---

const CodeBlock: React.FC<{ language?: string; children: string }> = ({ language, children }) => {
  const styles = useStyles();
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(children).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className={styles.codeBlockContainer}>
      <div className={styles.codeBlockHeader}>
        <span>{language || 'code'}</span>
        <Button
          icon={copied ? <CheckmarkRegular /> : <CopyRegular />}
          appearance="transparent"
          size="small"
          className={styles.copyButton}
          onClick={handleCopy}
        >
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <SyntaxHighlighter
        language={language || 'text'}
        style={oneDark}
        customStyle={{ margin: 0, borderRadius: 0, fontSize: '13px' }}
        wrapLongLines
      >
        {children}
      </SyntaxHighlighter>
    </div>
  );
};

const markdownComponents = (styles: ReturnType<typeof useStyles>) => ({
  code({ node, className, children, ...props }: any) {
    const match = /language-(\w+)/.exec(className || '');
    const isBlock = props.inline === undefined ? (typeof children === 'string' && children.includes('\n')) : !props.inline;

    if (match || isBlock) {
      return (
        <CodeBlock language={match?.[1]}>
          {String(children).replace(/\n$/, '')}
        </CodeBlock>
      );
    }
    return <code className={styles.inlineCode} {...props}>{children}</code>;
  },
  pre({ children }: any) {
    return <>{children}</>;
  },
});

// --- Helpers ---

const formatTimestamp = (isoString: string): string => {
  try {
    if (!isoString) return "";
    const date = new Date(isoString);
    if (isNaN(date.getTime()) || date.getFullYear() < 2000) return "";
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return "";
  }
};

// --- Typing indicator ---

const TypingIndicator: React.FC<{ streamingSpeed?: number }> = ({ streamingSpeed }) => {
  const styles = useStyles();
  return (
    <div className={styles.sendingIndicator}>
      <div className={styles.typingDots}>
        <span style={{ animation: 'pulse 1.4s infinite', animationDelay: '0s', width: 6, height: 6, borderRadius: '50%', background: tokens.colorBrandForeground1, display: 'inline-block' }} />
        <span style={{ animation: 'pulse 1.4s infinite', animationDelay: '0.2s', width: 6, height: 6, borderRadius: '50%', background: tokens.colorBrandForeground1, display: 'inline-block' }} />
        <span style={{ animation: 'pulse 1.4s infinite', animationDelay: '0.4s', width: 6, height: 6, borderRadius: '50%', background: tokens.colorBrandForeground1, display: 'inline-block' }} />
      </div>
      <span>Thinking...</span>
      {streamingSpeed !== undefined && streamingSpeed > 0 && (
        <span className={styles.speedIndicator}>{streamingSpeed} tok/s</span>
      )}
      <style>{`@keyframes pulse { 0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); } 40% { opacity: 1; transform: scale(1.2); } }`}</style>
    </div>
  );
};

// --- Landing Page ---

const LandingPage: React.FC = () => {
  const styles = useStyles();
  return (
    <div className={styles.landingPage}>
      <ChatSparkleRegular className={styles.landingIcon} />
      <Text as="h2" size={700} weight="semibold" className={styles.landingTitle}>
        Welcome to Pace AI
      </Text>
      <Text className={styles.landingSubtitle}>
        Select an agent from the sidebar to start a conversation, or create a new one tailored to your needs.
      </Text>
      <div className={styles.starterGrid}>
        <div className={styles.starterCard}>
          <ChatRegular className={styles.starterIcon} />
          <div>
            <Text weight="semibold" size={200}>Chat with Agents</Text>
            <Text className={styles.starterText} as="p">Ask questions, get analysis, draft documents</Text>
          </div>
        </div>
        <div className={styles.starterCard}>
          <CodeRegular className={styles.starterIcon} />
          <div>
            <Text weight="semibold" size={200}>Code Assistance</Text>
            <Text className={styles.starterText} as="p">Get help writing, reviewing, and debugging code</Text>
          </div>
        </div>
        <div className={styles.starterCard}>
          <PeopleRegular className={styles.starterIcon} />
          <div>
            <Text weight="semibold" size={200}>Custom Agents</Text>
            <Text className={styles.starterText} as="p">Build agents with tailored instructions and models</Text>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- ChatWindow ---

export interface ChatWindowProps {
  activeAgentId: string | null;
  activeAgent: Agent | null;
  messages: Message[];
  isLoadingMessages: boolean;
  isSendingMessage: boolean;
  chatError: string | null;
  sendMessage: (messageContent: string) => Promise<void>;
  stopGeneration: () => void;
  clearConversation: () => void;
  regenerateLastResponse: () => Promise<void>;
  editAndResend: (messageId: string, newContent: string) => Promise<void>;
  availableModels: ModelConfig[];
  selectedModel: string;
  onModelChange: (model: string) => void;
  isLoadingModels: boolean;
  sessionTokens: TokenUsage;
  userName?: string;
  profilePhoto?: string;
  streamingSpeed: number;
}

export const ChatWindow: React.FC<ChatWindowProps> = ({
  activeAgentId, activeAgent, messages,
  isLoadingMessages, isSendingMessage, chatError,
  sendMessage, stopGeneration, clearConversation,
  regenerateLastResponse, editAndResend,
  availableModels, selectedModel, onModelChange, isLoadingModels, sessionTokens,
  userName, profilePhoto, streamingSpeed,
}) => {
  const styles = useStyles();
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [copiedMsgId, setCopiedMsgId] = useState<string | null>(null);
  const [editingMsgId, setEditingMsgId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  // Improved auto-resize: grow until max, then show scrollbar
  const autoResize = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = '44px';
    const maxHeight = 192;
    const newHeight = Math.min(ta.scrollHeight, maxHeight);
    ta.style.height = newHeight + 'px';
    ta.style.overflowY = ta.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  useEffect(() => { autoResize(); }, [input, autoResize]);

  const handleSendMessage = async () => {
    if (!input.trim()) return;
    const messageToSend = input;
    setInput('');
    if (textareaRef.current) {
      textareaRef.current.style.height = '44px';
      textareaRef.current.style.overflowY = 'hidden';
    }
    await sendMessage(messageToSend);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleCopyMessage = (msg: Message) => {
    navigator.clipboard.writeText(msg.content).then(() => {
      setCopiedMsgId(msg.id);
      setTimeout(() => setCopiedMsgId(null), 2000);
    });
  };

  const handleStartEdit = (msg: Message) => {
    setEditingMsgId(msg.id);
    setEditContent(msg.content);
  };

  const handleSaveEdit = async () => {
    if (!editingMsgId || !editContent.trim()) return;
    setEditingMsgId(null);
    await editAndResend(editingMsgId, editContent);
  };

  const handleCancelEdit = () => {
    setEditingMsgId(null);
    setEditContent('');
  };

  const handleExportMarkdown = () => {
    let md = `# Conversation with ${activeAgent?.name || 'Assistant'}\n`;
    md += `*Model: ${selectedModel} | Exported: ${new Date().toLocaleString()}*\n\n---\n\n`;
    messages.forEach(msg => {
      const role = msg.role === 'user' ? '**You**' : `**${activeAgent?.name || 'Assistant'}**`;
      md += `### ${role} (${formatTimestamp(msg.createdAt)})\n\n${msg.content}\n\n---\n\n`;
    });
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pacechat-${Date.now()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Cost calculation
  const selectedModelConfig = availableModels.find(m => m.model === selectedModel);
  const sessionCost = selectedModelConfig ? (
    (sessionTokens.promptTokens / 1000) * (selectedModelConfig.costPer1kInputTokens || 0) +
    (sessionTokens.completionTokens / 1000) * (selectedModelConfig.costPer1kOutputTokens || 0)
  ) : null;

  const mdComponents = markdownComponents(styles);

  const isLastAssistant = (idx: number) =>
    messages[idx]?.role === 'assistant' &&
    !messages.slice(idx + 1).some(m => m.role === 'assistant');

  // Avatar props for agent
  const agentAvatarProps = activeAgent?.avatar
    ? { image: { src: activeAgent.avatar } }
    : { name: activeAgent?.name || 'A', color: 'brand' as const };

  // Avatar props for user
  const userAvatarProps = profilePhoto
    ? { image: { src: profilePhoto } }
    : { name: userName || 'U', color: 'colorful' as const };

  return (
    <div className={styles.chatWindow}>
      {isLoadingMessages && (
        <div className={styles.loadingSpinner}><Spinner label="Loading chat..." /></div>
      )}
      {chatError && !isLoadingMessages && (
        <MessageBar intent="error" className={styles.errorMessage}>
          <MessageBarBody>
            <MessageBarTitle>Error</MessageBarTitle>
            {chatError}
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Toolbar */}
      {activeAgentId && !isLoadingMessages && !chatError && (
        <div className={styles.chatToolbar}>
          <div className={styles.toolbarLeft}>
            {/* Agent info */}
            {activeAgent && (
              <div className={styles.agentInfoRow}>
                <Avatar {...agentAvatarProps} size={24} />
                <Text weight="semibold" size={300}>{activeAgent.name}</Text>
                {activeAgent.instructions && (
                  <Popover>
                    <PopoverTrigger disableButtonEnhancement>
                      <Tooltip content="View system prompt" relationship="label">
                        <Button icon={<InfoRegular />} appearance="transparent" size="small" className={styles.actionButton} />
                      </Tooltip>
                    </PopoverTrigger>
                    <PopoverSurface style={{ maxWidth: 400, maxHeight: 300, overflow: 'auto' }}>
                      <Text size={200} style={{ whiteSpace: 'pre-wrap' }}>{activeAgent.instructions}</Text>
                    </PopoverSurface>
                  </Popover>
                )}
              </div>
            )}

            {/* Model pills */}
            <div className={styles.modelPillContainer}>
              {availableModels.map(m => (
                <Tooltip content={m.description || m.model} relationship="description" key={m.id}>
                  <Button
                    appearance={selectedModel === m.model ? 'primary' : 'subtle'}
                    size="small"
                    shape="circular"
                    onClick={() => onModelChange(m.model)}
                    disabled={isSendingMessage || isLoadingModels}
                    className={styles.modelPill}
                  >
                    {m.name}
                  </Button>
                </Tooltip>
              ))}
            </div>
          </div>

          <div className={styles.toolbarRight}>
            {/* Token + cost display */}
            {sessionTokens.totalTokens > 0 && (
              <div className={styles.tokenDisplay}>
                <span className={styles.tokenBadge}>
                  <ArrowUpRegular fontSize={12} />
                  {sessionTokens.promptTokens.toLocaleString()}
                </span>
                <span className={styles.tokenBadge}>
                  <ArrowDownRegular fontSize={12} />
                  {sessionTokens.completionTokens.toLocaleString()}
                </span>
                {sessionCost !== null && sessionCost > 0 && (
                  <span className={styles.costBadge}>
                    ${sessionCost.toFixed(4)}
                  </span>
                )}
              </div>
            )}

            {/* Actions */}
            {messages.length > 0 && (
              <>
                <Tooltip content="Export as Markdown" relationship="label">
                  <Button icon={<ArrowDownloadRegular />} appearance="subtle" size="small" onClick={handleExportMarkdown} className={styles.actionButton} />
                </Tooltip>
                <Tooltip content="New Chat (Ctrl+N)" relationship="label">
                  <Button icon={<AddRegular />} appearance="subtle" size="small" onClick={clearConversation} className={styles.actionButton} />
                </Tooltip>
              </>
            )}
          </div>
        </div>
      )}

      {/* Landing page - no agent selected */}
      {!activeAgentId && !isLoadingMessages && !chatError && <LandingPage />}

      {/* Messages */}
      {activeAgentId && !isLoadingMessages && !chatError && (
        <div className={styles.chatMessages} aria-live="polite" aria-atomic="false">
          {messages.length === 0 && (
            <div className={styles.emptyChatContainer}>
              <ChatSparkleRegular className={styles.emptyChatIcon} />
              <Text size={400} weight="semibold">Start a conversation</Text>
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                Type a message below to begin chatting with {activeAgent?.name || 'this agent'}.
              </Text>
            </div>
          )}
          {messages.map((msg, idx) => {
            const isUser = msg.role === 'user';
            const isEditing = editingMsgId === msg.id;

            return (
              <div
                key={msg.id}
                className={`${styles.messageRow} ${isUser ? styles.messageRowUser : ''} ${styles.messageRowHover}`}
              >
                <Avatar
                  {...(isUser ? userAvatarProps : agentAvatarProps)}
                  size={28}
                  className={styles.messageAvatar}
                />
                <div className={`${styles.messageColumn} ${isUser ? styles.messageColumnUser : ''}`}>
                  {isEditing ? (
                    <>
                      <textarea
                        value={editContent}
                        onChange={e => setEditContent(e.target.value)}
                        className={styles.editArea}
                        autoFocus
                      />
                      <div className={styles.editActions}>
                        <Button size="small" appearance="primary" onClick={handleSaveEdit} disabled={!editContent.trim()}>
                          Save & Send
                        </Button>
                        <Button size="small" appearance="subtle" icon={<DismissRegular />} onClick={handleCancelEdit}>
                          Cancel
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className={`${styles.messageBubble} ${isUser ? styles.userMessage : styles.assistantMessage}`}>
                        {!isUser && activeAgent && (
                          <Text size={200} weight="semibold" className={styles.agentNameLabel}>
                            {activeAgent.name}
                          </Text>
                        )}
                        <div className={styles.messageContent}>
                          <ReactMarkdown components={mdComponents}>
                            {msg.content || (isSendingMessage && !isUser ? '' : '\u200B')}
                          </ReactMarkdown>
                        </div>
                      </div>
                      <div className={styles.messageFooter}>
                        <Caption1 style={{ color: tokens.colorNeutralForeground4 }}>
                          {formatTimestamp(msg.createdAt)}
                        </Caption1>
                        {msg.tokenUsage && (
                          <Caption1 style={{ color: tokens.colorNeutralForeground4 }}>
                            {msg.tokenUsage.totalTokens.toLocaleString()} tokens
                          </Caption1>
                        )}
                        <div className={`${styles.messageActions} msgActions`}>
                          <Tooltip content="Copy" relationship="label">
                            <Button
                              icon={copiedMsgId === msg.id ? <CheckmarkRegular /> : <CopyRegular />}
                              appearance="transparent"
                              size="small"
                              className={styles.actionButton}
                              onClick={() => handleCopyMessage(msg)}
                            />
                          </Tooltip>
                          {isUser && !isSendingMessage && (
                            <Tooltip content="Edit" relationship="label">
                              <Button
                                icon={<EditRegular />}
                                appearance="transparent"
                                size="small"
                                className={styles.actionButton}
                                onClick={() => handleStartEdit(msg)}
                              />
                            </Tooltip>
                          )}
                          {!isUser && isLastAssistant(idx) && !isSendingMessage && (
                            <Tooltip content="Regenerate" relationship="label">
                              <Button
                                icon={<ArrowSyncRegular />}
                                appearance="transparent"
                                size="small"
                                className={styles.actionButton}
                                onClick={regenerateLastResponse}
                              />
                            </Tooltip>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
          {isSendingMessage && <TypingIndicator streamingSpeed={streamingSpeed} />}
          <div ref={messagesEndRef} />
        </div>
      )}

      {/* Input area */}
      {activeAgentId && (
        <div className={styles.chatInputArea}>
          <div className={styles.inputRow}>
            <div className={styles.textareaWrapper}>
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your message... (Shift+Enter for new line)"
                disabled={isSendingMessage || !activeAgentId || isLoadingMessages}
                className={styles.textarea}
                rows={1}
              />
            </div>
            {isSendingMessage ? (
              <Tooltip content="Stop generating (Esc)" relationship="label">
                <Button
                  icon={<StopRegular />}
                  appearance="outline"
                  onClick={stopGeneration}
                  className={styles.stopButton}
                  aria-label="Stop generating"
                />
              </Tooltip>
            ) : (
              <Tooltip content="Send message" relationship="label">
                <Button
                  icon={<SendRegular />}
                  appearance="primary"
                  onClick={handleSendMessage}
                  disabled={!input.trim() || !activeAgentId || isLoadingMessages}
                  className={styles.sendButton}
                  aria-label="Send message"
                />
              </Tooltip>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
